import React, {forwardRef, useState, useMemo} from 'react'
import Select, {components} from 'react-select'
import ChevronDown from '../icons/arrows/ChevronDown'
import PropTypes from 'prop-types'
import {useIntl} from 'react-intl'
import {Box, chakra, Flex, Text, useMultiStyleConfig} from '@chakra-ui/react'
import SearchIcon from '../icons/action/SearchIcon'
import {remCalc} from '../../theme/_utils'
import {DropdownTheme} from '../../theme/components/base/dropdown'

const DropdownIndicator = (props) => {
    return (
        <components.DropdownIndicator {...props} tabIndex={-1}>
            <ChevronDown width={remCalc(16)} height={remCalc(16)} />
        </components.DropdownIndicator>
    )
}

DropdownIndicator.propTypes = {selectProps: PropTypes.any}

const Placeholder = (props) => {
    const {formatMessage} = useIntl()
    const onSearchPlaceholder =
        props.selectProps.menuIsOpen && props.selectProps.isSearchable ? (
            <Box as="span">
                <SearchIcon width={remCalc(14)} mr="0.625rem" ml="0.125rem" />
                <Text display="inline">
                    {formatMessage({
                        defaultMessage: 'Please select a value or type something.',
                        id: 'user_your_details_fields.error.please_select_value_or_type',
                    })}
                </Text>
            </Box>
        ) : (
            props.selectProps.placeholder
        )

    return <components.Placeholder {...props}>{onSearchPlaceholder}</components.Placeholder>
}

Placeholder.propTypes = {selectProps: PropTypes.any}

const ChakraSelect = chakra(Select)
const Dropdown = forwardRef(
    (
        {
            isSearchable,
            isDisabled,
            isInvalid,
            placeholder = '',
            onChange,
            name,
            label,
            rules,
            value,
            options,
            variant,
            ...rest
        },
        ref,
    ) => {
        const [inputValue, setInputValue] = useState('')
        const styles = useMultiStyleConfig('Dropdown', {
            variant: isInvalid ? 'invalid' : variant,
        })
        //const [isSelectOpen, setIsSelectOpen] = useState(false)
        const {formatMessage} = useIntl()

        let objValue = value
        if (typeof value === 'string') {
            objValue = options?.find((option) => option.value === value) || null
        }

        const filteredSortedOptions = useMemo(() => {
            if (!inputValue) {
                return [...options].sort((a, b) => a.label.localeCompare(b.label))
            }

            const input = inputValue.toLowerCase()

            const startsWithInput = []
            const containsInput = []

            for (const option of options) {
                const label = option.label.toLowerCase()
                if (label.startsWith(input)) {
                    startsWithInput.push(option)
                } else if (label.includes(input)) {
                    containsInput.push(option)
                }
            }

            startsWithInput.sort((a, b) => a.label.localeCompare(b.label))
            containsInput.sort((a, b) => a.label.localeCompare(b.label))

            return [...startsWithInput, ...containsInput]
        }, [options, inputValue])

        // const placeholderCustom =
        //     placeholder ??
        //     formatMessage({
        //         defaultMessage: 'Please select a value',
        //         id: 'form.error.please_select_a_value',
        //     })

        return (
            <Flex {...styles.container} role="group">
                <Box __css={styles} w="100%">
                    <ChakraSelect
                        {...rest}
                        ref={ref}
                        inputId={`select-${name}`}
                        instanceId={name}
                        id={`container-${name}`}
                        placeholder={placeholder}
                        components={{DropdownIndicator}}
                        className="react-select-container"
                        classNamePrefix="react-select"
                        isSearchable={isSearchable ?? false}
                        value={objValue}
                        inputValue={inputValue}
                        onInputChange={(inputValue) => setInputValue(inputValue)}
                        isDisabled={isDisabled ?? false}
                        // onMenuOpen={() => setIsSelectOpen(true)}
                        // onMenuClose={() => setIsSelectOpen(false)}
                        onChange={onChange}
                        theme={DropdownTheme(styles)}
                        aria-label={formatMessage({
                            defaultMessage: 'Please select a value',
                            id: 'form.error.please_select_a_value',
                        })}
                        options={filteredSortedOptions}
                    />
                </Box>
                <Text
                    {...styles.label}
                    {...(placeholder || objValue || inputValue ? {} : styles.labelActive)}
                >
                    {label}
                    {rules?.required && ' *'}
                </Text>
            </Flex>
        )
    },
)

Dropdown.displayName = 'Dropdown'
Dropdown.propTypes = {
    placeholder: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    value: PropTypes.any,
    isSearchable: PropTypes.bool,
    isDisabled: PropTypes.bool,
    isInvalid: PropTypes.bool,
    name: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    options: PropTypes.array.isRequired,
    onChange: PropTypes.func,
    variant: PropTypes.string,
    rules: PropTypes.object,
}

export default Dropdown
